import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Home, BarChart3, ClipboardList, GitCompare, CalendarHeart, Shield, LogOut } from "lucide-react";

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = [
    { to: "/", label: "Inicio", icon: Home, testId: "nav-home" },
    { to: "/log", label: "Registro", icon: ClipboardList, testId: "nav-log" },
    { to: "/dashboard", label: "Dashboard", icon: BarChart3, testId: "nav-dashboard" },
    { to: "/calendario", label: "Calendario", icon: CalendarHeart, testId: "nav-calendar" },
    { to: "/correlations", label: "Patrones", icon: GitCompare, testId: "nav-correlations" },
  ];
  if (user && user.role === "admin") {
    items.push({ to: "/admin", label: "Admin", icon: Shield, testId: "nav-admin" });
  }

  return (
    <div className="min-h-screen">
      {/* Top header (desktop) */}
      <header className="sticky top-0 z-30 tk-glass border-b border-cream-100/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 group"
            data-testid="header-logo"
          >
            <div className="relative h-9 w-9 rounded-2xl bg-sage-500 flex items-center justify-center shadow-sm">
              <span className="text-white font-outfit font-semibold text-base">t</span>
            </div>
            <span className="font-outfit text-lg tracking-tight text-ink-900 hidden sm:block">trackeate</span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testId}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-4 py-2 rounded-full font-dmsans text-sm transition ${
                    isActive
                      ? "bg-sage-500 text-white"
                      : "text-ink-900 hover:bg-sage-50"
                  }`
                }
              >
                <it.icon size={16} />
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && (
              <div className="hidden sm:flex flex-col text-right mr-1">
                <span className="font-dmsans text-xs text-ink-600">Hola,</span>
                <span className="font-outfit text-sm text-ink-900 -mt-0.5" data-testid="header-user-name">
                  {user.name}
                </span>
              </div>
            )}
            <button
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="h-9 w-9 rounded-full tk-glass flex items-center justify-center text-ink-600 hover:text-ink-900 transition"
              aria-label="Cerrar sesión"
              data-testid="header-logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-28 md:pb-12">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 tk-glass border-t border-cream-100/60">
        <div className="flex items-stretch">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`${it.testId}-mobile`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-1 py-2.5 font-dmsans text-[11px] transition ${
                  isActive ? "text-sage-600" : "text-ink-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <it.icon size={20} strokeWidth={isActive ? 2.4 : 1.7} />
                  <span>{it.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
